import React, { useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSurveyStore } from '../surveyutilities/surveyStore';
import CustomDialog from './smallcomponents/CustomDialog.jsx';

const IconAction = ({ id, tooltip, className = '', onClick, children }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`survey-editor-${id}`}>{tooltip}</Tooltip>}
    >
        <button
            type="button"
            className={`ecx-survey-editor__icon-btn${className ? ` ${className}` : ''}`}
            aria-label={tooltip}
            onClick={onClick}
        >
            {children}
        </button>
    </OverlayTrigger>
);

function EcxSurveyTopbar({ onSave, onPublish }) {
    const exportToJSON = useSurveyStore((state) => state.exportToJSON);
    const loadFromJSON = useSurveyStore((state) => state.loadFromJSON);
    const resetStore = useSurveyStore((state) => state.resetStore);
    const togglePreviewMode = useSurveyStore((state) => state.togglePreviewMode);
    const isPreviewMode = useSurveyStore((state) => state.isPreviewMode);
    const [jsonContent, setJsonContent] = useState(null);
    const [importJson, setImportJson] = useState('');
    const [copied, setCopied] = useState(false);
    const [pasted, setPasted] = useState(false);
    const dialogRef = useRef();
    const importDialogRef = useRef();

    const exportText = useMemo(
        () => (jsonContent ? JSON.stringify(jsonContent, null, 2) : ''),
        [jsonContent]
    );

    const importLineCount = useMemo(() => {
        if (!importJson.trim()) return 0;
        return importJson.split('\n').length;
    }, [importJson]);

    const handleExportJson = () => {
        setCopied(false);
        setJsonContent(exportToJSON());
        dialogRef.current.handleOpen();
    };

    const handleImportJson = () => {
        setPasted(false);
        importDialogRef.current.handleOpen();
    };

    const handleCopyExport = async () => {
        if (!exportText) return;
        try {
            await navigator.clipboard.writeText(exportText);
            setCopied(true);
            toast.success('JSON copied to clipboard');
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            toast.error('Failed to copy JSON');
        }
    };

    const handlePasteImport = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text?.trim()) {
                toast.info('Clipboard is empty');
                return;
            }
            setImportJson(text);
            setPasted(true);
            toast.success('JSON pasted from clipboard');
            window.setTimeout(() => setPasted(false), 1800);
        } catch (error) {
            toast.error('Unable to read clipboard. Paste manually instead.');
        }
    };

    const handleClearImport = () => {
        setImportJson('');
        setPasted(false);
    };

    const handleImportConfirm = () => {
        if (!importJson.trim()) {
            toast.error('Paste survey JSON before importing');
            return;
        }
        try {
            const parsedJson = JSON.parse(importJson);
            loadFromJSON(parsedJson);
            setImportJson('');
            setPasted(false);
            importDialogRef.current.handleClose();
            toast.success('Survey JSON imported');
        } catch (error) {
            toast.error('Invalid JSON format. Please check your input.');
        }
    };

    const handleResetClick = () => {
        if (
            window.confirm(
                'Are you sure you want to reset the canvas? This will clear all questions and settings.'
            )
        ) {
            resetStore();
        }
    };

    const ensureHasQuestions = () => {
        const { questions } = exportToJSON();
        if (!Array.isArray(questions) || questions.length === 0) {
            toast.error('Add at least one question before saving the survey');
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!ensureHasQuestions()) return;
        const surveyData = exportToJSON();
        if (!surveyData.survey.id) {
            surveyData.survey.id = null;
        }
        if (onSave) {
            onSave(surveyData);
        }
    };

    const handlePublish = () => {
        if (!ensureHasQuestions()) return;
        if (onPublish) {
            onPublish();
        }
    };

    return (
        <>
            <div className="ecx-survey-editor__topbar">
                <div>
                    <h2 className="ecx-survey-editor__title">Survey editor</h2>
                    <span className="ecx-survey-editor__title-meta">
                        {isPreviewMode
                            ? 'Preview mode — review how patients will see this survey'
                            : 'Add questions, configure settings, then save or publish'}
                    </span>
                </div>

                <div className="ecx-survey-editor__actions">
                    <IconAction id="import" tooltip="Import JSON" onClick={handleImportJson}>
                        <i className="bi bi-file-earmark-arrow-down" />
                    </IconAction>

                    <IconAction id="export" tooltip="Export JSON" onClick={handleExportJson}>
                        <i className="bi bi-file-earmark-arrow-up" />
                    </IconAction>

                    <IconAction
                        id="preview"
                        tooltip={isPreviewMode ? 'Exit preview' : 'Preview survey'}
                        className={isPreviewMode ? 'is-active' : ''}
                        onClick={togglePreviewMode}
                    >
                        <i className={`bi ${isPreviewMode ? 'bi-pencil-square' : 'bi-eye'}`} />
                    </IconAction>

                    <IconAction id="reset" tooltip="Reset canvas" onClick={handleResetClick}>
                        <i className="bi bi-arrow-counterclockwise" />
                    </IconAction>

                    {onSave && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="survey-editor-save">Save as draft</Tooltip>}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary rounded-pill"
                                aria-label="Save as draft"
                                onClick={handleSave}
                            >
                                <i className="bi bi-save me-1" />
                                Save
                            </button>
                        </OverlayTrigger>
                    )}
                    {onPublish && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="survey-editor-publish">Publish survey</Tooltip>}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-primary rounded-pill"
                                aria-label="Publish survey"
                                onClick={handlePublish}
                            >
                                <i className="bi bi-send-check me-1" />
                                Publish
                            </button>
                        </OverlayTrigger>
                    )}
                </div>
            </div>

            <CustomDialog
                ref={dialogRef}
                title="Export JSON"
                description="Copy this survey definition to reuse or back up your work."
                size="lg"
                confirmText={copied ? 'Copied' : 'Copy JSON'}
                confirmIcon={copied ? 'bi-check2' : 'bi-clipboard'}
                onConfirm={handleCopyExport}
                headerActions={
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={handleCopyExport}
                    >
                        <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                }
            >
                <div className="ecx-survey-export">
                    <div className="ecx-survey-export__toolbar">
                        <span className="ecx-page-chip">
                            <i className="bi bi-braces" />
                            Survey JSON
                        </span>
                        <span className="ecx-survey-export__meta">
                            {exportText ? `${exportText.split('\n').length} lines` : 'No data'}
                        </span>
                    </div>
                    <pre className="ecx-survey-export__code">{exportText || '//'}</pre>
                </div>
            </CustomDialog>

            <CustomDialog
                ref={importDialogRef}
                title="Import JSON"
                description="Paste a survey JSON export to load it into the editor."
                size="lg"
                confirmText="Import JSON"
                confirmIcon="bi-file-earmark-arrow-down"
                onConfirm={handleImportConfirm}
                headerActions={
                    <>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={handleClearImport}
                            disabled={!importJson}
                        >
                            <i className="bi bi-eraser me-1" />
                            Clear
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-pill"
                            onClick={handlePasteImport}
                        >
                            <i className={`bi ${pasted ? 'bi-check2' : 'bi-clipboard-plus'} me-1`} />
                            {pasted ? 'Pasted' : 'Paste'}
                        </button>
                    </>
                }
            >
                <div className="ecx-survey-export">
                    <div className="ecx-survey-export__toolbar">
                        <span className="ecx-page-chip">
                            <i className="bi bi-braces" />
                            Survey JSON
                        </span>
                        <span className="ecx-survey-export__meta">
                            {importLineCount
                                ? `${importLineCount} line${importLineCount === 1 ? '' : 's'}`
                                : 'Paste JSON below'}
                        </span>
                    </div>
                    <textarea
                        id="survey-import-json"
                        className="ecx-survey-export__code ecx-survey-import__editor"
                        value={importJson}
                        onChange={(e) => setImportJson(e.target.value)}
                        placeholder='{"survey": {...}, "questions": [...]}'
                        spellCheck={false}
                        aria-label="Survey JSON"
                    />
                </div>
            </CustomDialog>
        </>
    );
}

export default EcxSurveyTopbar;
